/**
 * Submits a Shopify {% form %} in the background so the page does not reload.
 *
 * Wraps any form that posts to a Shopify endpoint returning HTML (customer, contact).
 * Success is read from the URL Shopify redirects to - it appends `customer_posted=true`
 * or `contact_posted=true` - rather than by scraping the response, which is the only
 * signal that does not depend on markup we do not control.
 *
 * Progressive enhancement: the submit handler is the only thing that stops the native
 * post, so if this script fails to load the form still works the old way. Constraint
 * validation is the browser's job - a `submit` event only fires once it passes.
 *
 * Attributes:
 *   success-message  text shown after a successful post
 *   error-message    fallback text when the post fails and no server message is found
 *   error-selector   selector for the server's own error text in the returned HTML
 *   network-error-message  text shown when the request itself fails
 *
 * Expects a `[data-async-message]` element to write into.
 */
class AsyncForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  get #message() {
    return this.querySelector('[data-async-message]');
  }

  get #submitButton() {
    return this.#form?.querySelector('[type="submit"], button:not([type])') ?? null;
  }

  #onSubmit = async (event) => {
    const form = this.#form;
    if (!form) return;

    event.preventDefault();

    const button = this.#submitButton;
    if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
      if (button.disabled) return; // already in flight
      button.disabled = true;
    }
    this.setAttribute('submitting', '');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      const posted = this.#wasPosted(response.url);

      if (posted) {
        form.hidden = true;
        this.#show(this.getAttribute('success-message') || '', true);
        return;
      }

      const html = await response.text();
      this.#show(this.#serverError(html) || this.#fallbackError(), false);
    } catch {
      // A thrown fetch is a transport problem, not bad input - do not blame the shopper's
      // email address for it.
      this.#show(this.#networkError(), false);
    } finally {
      this.removeAttribute('submitting');
      if (
        (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) &&
        !form.hidden
      ) {
        button.disabled = false;
      }
    }
  };

  /**
   * @param {string} url
   * @returns {boolean}
   */
  #wasPosted(url) {
    try {
      const params = new URL(url, window.location.href).searchParams;
      return params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Pulls Shopify's own validation text out of the re-rendered page, so the shopper sees
   * the real reason ("is invalid") rather than a generic apology.
   * @param {string} html
   * @returns {string}
   */
  #serverError(html) {
    const selector = this.getAttribute('error-selector');
    if (!selector) return '';

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const node of doc.querySelectorAll(selector)) {
        const text = node.textContent?.trim();
        if (text) return text;
      }
    } catch {
      // fall through to the generic message
    }
    return '';
  }

  #fallbackError() {
    return this.getAttribute('error-message') || 'Something went wrong. Please try again.';
  }

  #networkError() {
    return (
      this.getAttribute('network-error-message') ||
      "Sorry, that didn't send. Please check your connection and try again."
    );
  }

  /**
   * @param {string} text
   * @param {boolean} success
   */
  #show(text, success) {
    const target = this.#message;
    if (!target) return;

    target.textContent = text;
    target.hidden = false;
    target.classList.toggle('async-form__message--success', success);
    target.classList.toggle('async-form__message--error', !success);
    if (target instanceof HTMLElement) target.focus();
  }
}

if (!customElements.get('async-form')) {
  customElements.define('async-form', AsyncForm);
}
